import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { CpPortalContext, CpPortalMirrorValue } from '../../contexts/CpPortalContext';

function portalAuthHeaders(): Record<string, string> {
  const token = sessionStorage.getItem('portal_token');
  const h: Record<string, string> = { Accept: 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

/**
 * Loads CP mirror context for MM Admin and provides CpPortalContext to child routes.
 */
export const AdminCpMirrorProvider: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const parsedId = parseInt(clientId || '', 10);
  const [mirror, setMirror] = useState<CpPortalMirrorValue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!Number.isInteger(parsedId) || parsedId <= 0) {
      setError('Invalid channel partner id');
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `/api/v1/disbursement-clients/${parsedId}/cp-mirror/context`,
          { headers: portalAuthHeaders() },
        );
        const json = await res.json();
        if (!json.success) throw new Error(json.error || 'Failed to load partner context');
        if (cancelled) return;
        const d = json.data;
        setMirror({
          mode: 'admin-mirror',
          clientId: d.clientId,
          companyName: d.companyName,
          clientCode: d.clientCode,
          branding: d.branding || null,
          onboardingPhase: d.onboardingPhase || d.phase || 'operational',
          role: 'admin',
          revenueShareEnabled: !!d.revenueShareEnabled,
        });
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load partner context');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [parsedId]);

  if (!Number.isInteger(parsedId) || parsedId <= 0) {
    return <Navigate to="/admin/channel-partners" replace />;
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[var(--background)]">
        <div className="text-center">
          <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin text-[var(--primary)]" />
          <p className="text-sm text-[var(--muted-foreground)]">Loading channel partner view…</p>
        </div>
      </div>
    );
  }

  if (error || !mirror) {
    return (
      <div className="flex h-screen items-center justify-center bg-[var(--background)] p-6">
        <div className="max-w-md rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <AlertCircle className="mx-auto mb-3 h-8 w-8 text-red-600" />
          <p className="text-sm text-red-700">{error || 'Could not open channel partner view'}</p>
          <a
            href="/admin/channel-partners"
            className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[var(--primary)] hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Channel Partners
          </a>
        </div>
      </div>
    );
  }

  return (
    <CpPortalContext.Provider value={mirror}>
      <div className="flex h-screen flex-col overflow-hidden">
        <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          <div className="flex min-w-0 items-center gap-2">
            <span className="font-semibold">Admin mirror view</span>
            <span className="truncate font-mono text-xs text-amber-800">
              {mirror.clientCode}
            </span>
          </div>
          <a
            href="/admin/channel-partners"
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            All partners
          </a>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <Outlet />
        </div>
      </div>
    </CpPortalContext.Provider>
  );
};
